import { useLayoutEffect, useRef } from "react";

/**
 * Kort som kommer til, glir inn — og naboene glir med.
 *
 * NABOENE ER POENGET. Kortene ligger i rutenett som flyter: kommer det ett
 * til, flytter de som alt står der seg et godt stykke. Animerer man bare det
 * nye, er naboenes HOPP det eneste man ser, og da er bevegelsen verre enn
 * ingen.
 *
 * Derfor måles posisjonene før og etter hver tegning: hvert kort settes
 * tilbake dit det sto og slippes. Bare `transform` og `opacity` beveger seg,
 * aldri layout.
 *
 * Ingenting animeres ved første tegning — det første bildet skal vise flata i
 * ro, ikke en bevegelse som allerede er i gang.
 */

const VARIGHET = 220;

/*
 * Tvinger fram en opptegning, så nettleseren ser utgangspunktet før vi setter
 * sluttpunktet. Uten den skjer begge i samme omgang og det blir ingen
 * overgang.
 *
 * ET METODEKALL, IKKE EN FELTLESING. `void el.offsetWidth` ble fjernet av
 * minifikatoren — kilden hadde to, bundelen én — og da virket animasjonen i
 * utvikling, men ikke i det som ble lagt ut.
 */
function tvingOpptegning(el: HTMLElement): void {
  el.getBoundingClientRect();
}

/** Returnerer ref-setteren kortene skal registrere seg med. */
export function useGlidendeKort(): (id: string, el: HTMLElement | null) => void {
  const noder = useRef(new Map<string, HTMLElement>());
  const plass = useRef(new Map<string, DOMRect>());
  const foerste = useRef(true);

  useLayoutEffect(() => {
    const rolig = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    /* Kort som er borte fra dokumentet ryddes her, ikke i ref-setteren:
       React kaller den forrige ref-funksjonen med `null` ved HVER tegning,
       ikke bare når kortet forsvinner. */
    for (const [id, el] of noder.current) {
      if (!el.isConnected) {
        noder.current.delete(id);
        plass.current.delete(id);
      }
    }

    const naa = new Map<string, DOMRect>();
    for (const [id, el] of noder.current) naa.set(id, el.getBoundingClientRect());

    if (foerste.current) {
      foerste.current = false;
      plass.current = naa;
      return;
    }

    if (!rolig) {
      for (const [id, el] of noder.current) {
        const ny = naa.get(id);
        if (ny === undefined) continue;
        const gammel = plass.current.get(id);

        if (gammel === undefined) {
          /* Nytt kort. Aldri fra `scale(0)` — ingenting i verden dukker opp
             fra ingenting. */
          el.style.transition = "none";
          el.style.opacity = "0";
          el.style.transform = "scale(0.97)";
          tvingOpptegning(el);
          el.style.transition =
            `opacity ${VARIGHET}ms var(--ease-out), transform ${VARIGHET}ms var(--ease-out)`;
          el.style.opacity = "";
          el.style.transform = "";
          continue;
        }

        const dx = gammel.left - ny.left;
        const dy = gammel.top - ny.top;
        if (Math.abs(dx) < 1 && Math.abs(dy) < 1) continue;
        el.style.transition = "none";
        el.style.transform = `translate(${dx}px, ${dy}px)`;
        tvingOpptegning(el);
        el.style.transition = `transform ${VARIGHET}ms var(--ease-out)`;
        el.style.transform = "";
      }
    }

    plass.current = naa;
  });

  return (id, el) => { if (el !== null) noder.current.set(id, el); };
}
